import { useLayoutEffect, useRef, type ChangeEvent } from "react";

/**
 * Multi-line free text — the registered address, the liability arrangements.
 *
 * Carries the same caret rule as `TextField`: the value is controlled, so every keystroke
 * re-renders, and the caret has to be put back where it was. `<textarea>` supports the
 * selection APIs, so `selectionStart` is readable; where it is not, the caret goes to the
 * end of the value rather than to 0 (data dictionary §9).
 */

export interface TextAreaFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
  error?: string;
  placeholder?: string;
  required?: boolean;
  rows?: number;
}

export function TextAreaField({
  id,
  label,
  value,
  onChange,
  hint,
  error,
  placeholder,
  required = false,
  rows = 3,
}: TextAreaFieldProps) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const caret = useRef<number | null>(null);

  useLayoutEffect(() => {
    const input = ref.current;
    if (!input || caret.current === null) return;
    const position = Math.min(caret.current, input.value.length);
    input.setSelectionRange(position, position);
    caret.current = null;
  });

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    caret.current = event.target.selectionStart ?? event.target.value.length;
    onChange(event.target.value);
  };

  const describedBy = [hint ? `${id}-hint` : null, error ? `${id}-error` : null]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="field">
      <label className="f" htmlFor={id}>
        {label} {required ? <b>*</b> : null}
        {hint ? <i id={`${id}-hint`}> {hint}</i> : null}
      </label>
      <textarea
        ref={ref}
        id={id}
        className="t"
        rows={rows}
        value={value}
        placeholder={placeholder}
        onChange={handleChange}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
      />
      {error ? (
        <p className="err" id={`${id}-error`} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
